import { ApplicationEvent } from '~shared/modules/application-event/models';
import {
  ApplicationCategorySelectedEventType,
  ApplicationEventType,
  ApplicationNavigationEventType,
  ApplicationSearchEventSource,
  ApplicationSearchInteractionType,
  ApplicationSuggestionSelectedEventType,
} from '~shared/modules/application-event/constants';

export namespace GATrackingApplicationEventValidator {
  const eventTypes: ApplicationEventType[] = [
    ApplicationEventType.AddToCart,
    ApplicationEventType.AddToCartMoreThanInStock,
    ApplicationEventType.AlternativeProductsShow,
    ApplicationEventType.AssistedSales,
    ApplicationEventType.Authorization,
    ApplicationEventType.BannerClickExperiment,
    ApplicationEventType.ButtonClick,
    ApplicationEventType.CarouselItemsViewed,
    ApplicationEventType.CartUpdate,
    ApplicationEventType.CategorySelected,
    ApplicationEventType.CheckoutErrors,
    ApplicationEventType.CheckoutStep,
    ApplicationEventType.CheckoutStepper,
    ApplicationEventType.CreateAccount,
    ApplicationEventType.CreateAccountExperiment,
    ApplicationEventType.LogoutClick,
    ApplicationEventType.Navigation,
    ApplicationEventType.Notification,
    ApplicationEventType.Optimize,
    ApplicationEventType.PaymentFailure,
    ApplicationEventType.ProductClick,
    ApplicationEventType.ProductDetailsViewed,
    ApplicationEventType.FlixMediaContentViewed,
    ApplicationEventType.FlixMediaContentViewed,
    ApplicationEventType.FlixMediaContentScroll,
    ApplicationEventType.HomePageViewed,
    ApplicationEventType.PriceTypeChanged,
    ApplicationEventType.ProductListViewed,
    ApplicationEventType.Purchase,
    ApplicationEventType.PurchaseGA4,
    ApplicationEventType.RemoveFromCart,
    ApplicationEventType.Search,
    ApplicationEventType.CategoryPage,
    ApplicationEventType.CategoryPageViewed,
    ApplicationEventType.SearchInteraction,
    ApplicationEventType.SuggestionSelected,
    ApplicationEventType.CookieBanner,
    ApplicationEventType.CarouselClick,
    ApplicationEventType.CarouselButton,
    ApplicationEventType.PdpImageInteraction,
    ApplicationEventType.VolumePriceViewed,
    ApplicationEventType.PdpDescriptionInteraction,
    ApplicationEventType.PdpLoadbeeInteraction,
    ApplicationEventType.PdpCompareAllOffersClick,
    ApplicationEventType.PdpFurtherInformationInteraction,
    ApplicationEventType.Scrolling,
    ApplicationEventType.QuantityPickerInteraction,
    ApplicationEventType.BannerInteraction,
    ApplicationEventType.SeoFaqToggle,
    ApplicationEventType.SimilarProductsView,
    ApplicationEventType.ProductsSorted,
    ApplicationEventType.VolumePriceTableClicked,
    ApplicationEventType.PLPPaginationShowMoreClick,
    ApplicationEventType.OutOfStockNotifyClick,
    ApplicationEventType.ExploreSimilarProductsClick,
    ApplicationEventType.PdpBrandInteraction,
    ApplicationEventType.PdpSellerInteraction,
    ApplicationEventType.PdpOtherOffersInteraction,
    ApplicationEventType.PLPSeoText,
    ApplicationEventType.CartLinkClick,
    ApplicationEventType.OrderHistoryInteraction,
    ApplicationEventType.FilterAreaInteraction,
    ApplicationEventType.PLPPriceFilterInteraction,
    ApplicationEventType.PDPRegisterAsBusinessInteraction,
    ApplicationEventType.IdamSignUp,
    ApplicationEventType.MiniCartInteraction,
    ApplicationEventType.PageView,
    ApplicationEventType.LoadBeeButtonView,
    ApplicationEventType.ProceedToCheckout,
    ApplicationEventType.CarouselSwipeInteraction,
    ApplicationEventType.RegistrationType,
    ApplicationEventType.RegisterWithEmail,
    ApplicationEventType.OfferPriceSyncIssue,
    ApplicationEventType.NewsletterSubscription,
    ApplicationEventType.HubPageCardClick,
    ApplicationEventType.FeatureFlag,
    ApplicationEventType.SignUpToCheckoutTransition,
    ApplicationEventType.PdpOfferMismatch,
    ApplicationEventType.FlixMediaContentFailed,
    ApplicationEventType.FlixMediaFailed,
    ApplicationEventType.PDPFoodCategoryLink,
    ApplicationEventType.OptimizelyActivateExperiment,
    ApplicationEventType.OrderHistorySideMenuInteraction,
    ApplicationEventType.CustomLinkedGrid,
    ApplicationEventType.PromoImageLinkClick,
    ApplicationEventType.PdpWeeMoreDetailsClick,
    ApplicationEventType.SearchBanner,
    ApplicationEventType.EditorialLinkNavigationClick,
    ApplicationEventType.ContentCardClick,
    ApplicationEventType.ViewItemsList,
    ApplicationEventType.ViewPromotion,
    ApplicationEventType.SelectItem,
    ApplicationEventType.SelectPromotion,
    ApplicationEventType.AddPaymentInfo,
    ApplicationEventType.AddShippingInfo,
    ApplicationEventType.ReviewOrderInfo,
    ApplicationEventType.ViewCart,
    ApplicationEventType.RemoveItemFromCart,
    ApplicationEventType.BeginCheckout,
    ApplicationEventType.ViewItem,
    ApplicationEventType.AddToCartGa4,
    ApplicationEventType.ViewRecommendation,
    ApplicationEventType.SelectRecommendation,
    ApplicationEventType.InstallationServices,
    ApplicationEventType.InstallationServicesDetails,
    ApplicationEventType.InstallationServicesDetailsLinks,
    ApplicationEventType.ShowTooltipValueAddedService,
    ApplicationEventType.CallbackClick,
    ApplicationEventType.InvalidOfferForRegion,
    ApplicationEventType.ChangeDeliveryAddress,
    ApplicationEventType.BackToCart,
    ApplicationEventType.Search_0_results,
    ApplicationEventType.Search_0_results_click,
    ApplicationEventType.Search_ZRP_category_view,
    ApplicationEventType.Search_ZRP_other_buckets,
    ApplicationEventType.Search_ZRP_other_buckets_clicked,
    ApplicationEventType.ZRPCategoryClicked,
    ApplicationEventType.GA4General,
  ];

  export function isValid(event: ApplicationEvent): boolean {
    const type: ApplicationEventType = event.type;

    if (!isValidEventType(type)) {
      return false;
    }

    const payload = event.getPayload();
    if (!isValidNavigationEvent(type, payload)) {
      return false;
    }

    if (!isValidCategorySelectedEvent(type, payload)) {
      return false;
    }

    if (!isValidSearchEvent(type, payload)) {
      return false;
    }

    if (!isValidCategoryPageEvent(type, payload)) {
      return false;
    }

    if (!isValidCategoryPageViewedEvent(type, payload)) {
      return false;
    }

    if (!isValidSearchInteractionEvent(type, payload)) {
      return false;
    }

    if (!isValidSuggestionsEvent(type, payload)) {
      return false;
    }

    return true;
  }

  function isValidEventType(type: ApplicationEventType): boolean {
    return eventTypes.includes(type);
  }

  function isValidNavigationEvent(type: ApplicationEventType, payload: any): boolean {
    if (type !== ApplicationEventType.Navigation) {
      return true;
    }

    return [
      ApplicationNavigationEventType.Breadcrumb,
      ApplicationNavigationEventType.CheckoutStepNavigationClick,
      ApplicationNavigationEventType.FooterNavigationClick,
      ApplicationNavigationEventType.GlobalFooterClick,
      ApplicationNavigationEventType.GlobalHeaderClick,
      ApplicationNavigationEventType.NavigationBarClick,
      ApplicationNavigationEventType.NavigationUserAccountMenuClick,
      ApplicationNavigationEventType.PLPCategoryNavigation,
      ApplicationNavigationEventType.PLPCategoryNavigationExpanded,
      ApplicationNavigationEventType.TopNavigationClick,
    ].includes(payload.type);
  }

  function isValidCategorySelectedEvent(type: ApplicationEventType, payload: any): boolean {
    if (type !== ApplicationEventType.CategorySelected) {
      return true;
    }

    if (payload.type !== ApplicationCategorySelectedEventType.TopAngebote) {
      return true;
    }

    return false;
  }

  function isValidSearchEvent(type: ApplicationEventType, payload: any): boolean {
    if (type !== ApplicationEventType.Search) {
      return true;
    }

    return [
      ApplicationSearchEventSource.AutoCorrection,
      ApplicationSearchEventSource.Suggestions,
      ApplicationSearchEventSource.Header,
      ApplicationSearchEventSource.ZeroResult,
      ApplicationSearchEventSource.Filter,
      ApplicationSearchEventSource.FilterUnselected,
      ApplicationSearchEventSource.FilterRemove,
      ApplicationSearchEventSource.FilterRemoveAll,
      ApplicationSearchEventSource.Direct,
    ].includes(payload.source);
  }

  function isValidCategoryPageEvent(type: ApplicationEventType, payload: any): boolean {
    if (type !== ApplicationEventType.CategoryPage) {
      return true;
    }

    return [
      ApplicationSearchEventSource.Suggestions,
      ApplicationSearchEventSource.Header,
      ApplicationSearchEventSource.ZeroResult,
      ApplicationSearchEventSource.Filter,
      ApplicationSearchEventSource.FilterUnselected,
      ApplicationSearchEventSource.FilterRemove,
      ApplicationSearchEventSource.FilterRemoveAll,
      ApplicationSearchEventSource.Direct,
    ].includes(payload.source);
  }

  function isValidCategoryPageViewedEvent(type: ApplicationEventType, payload: any): boolean {
    if (type !== ApplicationEventType.CategoryPageViewed) {
      return true;
    }

    return [
      ApplicationSearchEventSource.Suggestions,
      ApplicationSearchEventSource.Header,
      ApplicationSearchEventSource.ZeroResult,
      ApplicationSearchEventSource.Filter,
      ApplicationSearchEventSource.FilterUnselected,
      ApplicationSearchEventSource.FilterRemove,
      ApplicationSearchEventSource.FilterRemoveAll,
      ApplicationSearchEventSource.Direct,
    ].includes(payload.source);
  }

  function isValidSearchInteractionEvent(type: ApplicationEventType, payload: any): boolean {
    if (type !== ApplicationEventType.SearchInteraction) {
      return true;
    }

    const interactionType: ApplicationSearchInteractionType = payload.type;

    return [
      ApplicationSearchInteractionType.BounceOfSearchBar,
      ApplicationSearchInteractionType.ClickInTheSearchBar,
    ].includes(interactionType);
  }

  function isValidSuggestionsEvent(type: ApplicationEventType, payload: any): boolean {
    if (type !== ApplicationEventType.SuggestionSelected) {
      return true;
    }

    const suggestionType: ApplicationSuggestionSelectedEventType = payload.type;
    return [
      ApplicationSuggestionSelectedEventType.AllPromoSuggestionSelected,
      ApplicationSuggestionSelectedEventType.CategorySuggestionSelected,
      ApplicationSuggestionSelectedEventType.HistorySuggestionSelected,
      ApplicationSuggestionSelectedEventType.PromoSuggestionSelected,
      ApplicationSuggestionSelectedEventType.TextSuggestionSelected,
    ].includes(suggestionType);
  }
}
